// The cart's RBP missed-products widget: `GET /api/v1/rbp/missed-products?entry=<KIND>:<depot>`.
// The wire shape is a LOCK-STEP COPY of rbp's `RbpMissedProductsResponse` (libs/modules/rbp
// contracts/dtos/rbp-core-range.dto.ts): every candidate for the entry's catalogue, each product
// once, by department name then code, unpriced and uncapped. The web drops what is in the cart and
// what has no price above zero, then caps each department.
//
// The widget is advisory and never an error the cart has to show: a refused caller (403) or a
// route not deployed (404) answers "no products", and any other failure answers `null` (unread),
// so the caller can ask again later.

import { withEntry } from './catalogue-entry';
import { API_PREFIX, apiFetch } from './shoplink-api';

export const MISSED_PRODUCTS_PATH = `${API_PREFIX}/rbp/missed-products`;

export interface MissedProduct {
  readonly productCode: string;
  readonly name: string | null;
  readonly departmentName: string | null;
  readonly packSize: string | null;
}

const nullableString = (value: unknown): value is string | null =>
  value === null || typeof value === 'string';

function parseProduct(value: unknown): MissedProduct | null {
  if (typeof value !== 'object' || value === null) return null;
  const { productCode, name, departmentName, packSize } = value as Record<string, unknown>;
  if (typeof productCode !== 'string' || productCode === '') return null;
  return {
    productCode,
    name: nullableString(name) ? name : null,
    departmentName: nullableString(departmentName) ? departmentName : null,
    packSize: nullableString(packSize) ? packSize : null,
  };
}

/** The widget's candidates for the entry; `[]` when refused, `null` when unread. Never rejects. */
export async function fetchMissedProducts(entryKey: string): Promise<MissedProduct[] | null> {
  try {
    const res = await apiFetch(withEntry(MISSED_PRODUCTS_PATH, entryKey));
    if (res.status === 403 || res.status === 404) return [];
    if (!res.ok) return null;
    const body: unknown = await res.json();
    const products =
      typeof body === 'object' && body !== null
        ? (body as { products?: unknown }).products
        : undefined;
    if (!Array.isArray(products)) return null;
    return products.flatMap((value) => parseProduct(value) ?? []);
  } catch {
    return null;
  }
}
